import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allTools } from '../../mocks/tools';
import { categories } from '../../mocks/categories';
import { blogPosts } from '../../mocks/blog-posts';
import { TOOL_LOGOS } from '../../mocks/tool-logos';

function ToolIconImg({ src, name }: { src: string; name: string }) {
  const [err, setErr] = useState(false);
  const initials = (() => {
    const words = name.replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  })();
  if (src && !err) {
    return (
      <img
        src={src}
        alt={name}
        width={32}
        height={32}
        className="w-8 h-8 object-contain"
        onError={() => setErr(true)}
      />
    );
  }
  return <span className="text-xs font-bold text-gray-300 select-none">{initials}</span>;
}

interface SearchDropdownProps {
  query: string;
  onClose: () => void;
  isOpen: boolean;
}

interface SearchResult {
  type: 'tool' | 'category' | 'article';
  id: string | number;
  name: string;
  description?: string;
  icon?: string;
  slug?: string;
}

export default function SearchDropdown({ query, onClose, isOpen }: SearchDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter results based on query
  const searchQuery = query.toLowerCase().trim();
  
  // Only show results if query is 2+ characters
  const shouldShowResults = searchQuery.length >= 2;

  const filteredTools = shouldShowResults
    ? allTools.filter(
        (tool) =>
          tool.name.toLowerCase().includes(searchQuery) ||
          tool.description.toLowerCase().includes(searchQuery) ||
          tool.category.toLowerCase().includes(searchQuery)
      ).slice(0, 4)
    : [];

  const filteredCategories = shouldShowResults
    ? categories.filter(
        (category) =>
          category.name.toLowerCase().includes(searchQuery) ||
          category.description.toLowerCase().includes(searchQuery)
      ).slice(0, 2)
    : [];

  const filteredBlogPosts = shouldShowResults
    ? blogPosts.filter(
        (post) =>
          post.title.toLowerCase().includes(searchQuery) ||
          post.description.toLowerCase().includes(searchQuery) ||
          post.category.toLowerCase().includes(searchQuery)
      ).slice(0, 2)
    : [];

  // Combine all results into a single array for keyboard navigation
  const allResults: SearchResult[] = [
    ...filteredTools.map(tool => ({
      type: 'tool' as const,
      id: tool.id,
      name: tool.name,
      description: tool.description,
      icon: TOOL_LOGOS[tool.slug] ?? '',
      slug: tool.slug
    })),
    ...filteredCategories.map(cat => ({
      type: 'category' as const,
      id: cat.id,
      name: cat.name,
      description: cat.description,
      icon: cat.icon
    })),
    ...filteredBlogPosts.map(post => ({
      type: 'article' as const,
      id: post.id,
      name: post.title,
      description: post.description,
      slug: post.slug
    }))
  ].slice(0, 8); // Limit to 8 total suggestions

  const hasResults = allResults.length > 0;

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen || !hasResults) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => (prev + 1) % allResults.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => (prev - 1 + allResults.length) % allResults.length);
          break;
        case 'Enter':
          event.preventDefault();
          if (allResults[selectedIndex]) {
            handleResultClick(allResults[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, hasResults, selectedIndex, allResults]);

  if (!isOpen || !shouldShowResults) {
    return null;
  }

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'tool') {
      navigate(`/tool/${result.slug}`);
    } else if (result.type === 'category') {
      const category = categories.find(c => c.id === result.id);
      if (category) {
        navigate(`/category/${category.slug}`);
      }
    } else if (result.type === 'article') {
      navigate(`/blog/${result.slug}`);
    }
    onClose();
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'tool':
        return 'Tool';
      case 'category':
        return 'Category';
      case 'article':
        return 'Article';
      default:
        return '';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'tool':
        return 'bg-teal-500/10 text-teal-400 border border-teal-500/20';
      case 'category':
        return 'bg-purple-500/10 text-purple-400 border border-purple-500/20';
      case 'article':
        return 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border border-gray-500/20';
    }
  };

  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === query.toLowerCase() ? (
            <strong key={i} className="font-bold text-teal-400">{part}</strong>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a1a] backdrop-blur-xl border border-[#333] rounded-lg shadow-2xl shadow-black/60 max-h-[500px] overflow-y-auto z-50"
      style={{
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      {!hasResults ? (
        <div className="p-8 text-center">
          <i className="ri-search-line text-4xl text-gray-600 mb-3"></i>
          <p className="text-gray-400 text-sm">No results found for "{query}"</p>
          <p className="text-gray-500 text-xs mt-2">Try different keywords or browse our directory</p>
        </div>
      ) : (
        <>
          <div className="p-2">
            {allResults.map((result, index) => (
              <button
                key={`${result.type}-${result.id}`}
                onClick={() => handleResultClick(result)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left group cursor-pointer ${
                  selectedIndex === index
                    ? 'bg-purple-500/20 border border-purple-500/30'
                    : 'hover:bg-purple-500/20 border border-transparent'
                }`}
              >
                {/* Icon/Logo */}
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg overflow-hidden bg-[#1a2235] border border-gray-700/40">
                  {result.type === 'tool' && result.icon ? (
                    <ToolIconImg src={result.icon} name={result.name} />
                  ) : result.type === 'category' && result.icon ? (
                    <i className={`${result.icon} text-xl text-teal-400`}></i>
                  ) : result.type === 'article' ? (
                    <i className="ri-article-line text-xl text-teal-400/60"></i>
                  ) : (
                    <span className="text-sm font-bold text-gray-300">{result.name.slice(0, 2).toUpperCase()}</span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-semibold truncate transition-colors ${
                    selectedIndex === index ? 'text-white' : 'text-white'
                  }`}>
                    {highlightMatch(result.name, searchQuery)}
                  </h4>
                  {result.description && (
                    <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">
                      {result.description}
                    </p>
                  )}
                </div>

                {/* Type Badge */}
                <div className={`px-2.5 py-1 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${getTypeColor(result.type)}`}>
                  {getTypeLabel(result.type)}
                </div>

                {/* Arrow Icon */}
                <i className={`ri-arrow-right-line flex-shrink-0 transition-colors ${
                  selectedIndex === index ? 'text-teal-400' : 'text-gray-600 group-hover:text-teal-400'
                }`}></i>
              </button>
            ))}
          </div>

          {/* View All Results Link */}
          <div className="border-t border-[#333] p-3">
            <button
              onClick={() => {
                navigate(`/search?q=${encodeURIComponent(query)}`);
                onClose();
              }}
              className="w-full py-2.5 px-4 bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <span>View all results for "{query}"</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {/* Keyboard Hints */}
          <div className="border-t border-[#333] px-4 py-2 flex items-center justify-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-gray-400">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-gray-400">Enter</kbd>
              Select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-gray-400">Esc</kbd>
              Close
            </span>
          </div>
        </>
      )}
    </div>
  );
}